import "./native-format-picker.css";
interface Format {
  id: string;
  category: string;
  title: string;
  width: number;
  height: number;
  unit: string;
  image: string;
  size: string;
  feature: string;
  badge?: string;
  widthMM?: number;
  heightMM?: number;
}
export interface FormatPickerPort {
  read(): { width: number; height: number; generation: number };
  choose(format: Format): void;
}
/** Original creator format picker markup, catalog, artwork and interactions; native draft adapter only. */
export function createFormatPicker(port: FormatPickerPort) {
  const DOCUMENT_DPI = 96;
  const el = (id: string) => document.getElementById(id)!;

  const trigger = el("screen-format-picker-open");
  if (!trigger) return;
  const assetRoot =
    "/templates/boxed/controllers/creatorenginepro/assets/native-format-v20/images/";
  const paper = (
    code: string,
    widthMM: number,
    heightMM: number,
    description: string,
  ) => [
    {
      id: code + "-portrait",
      category: "print",
      title: code.toUpperCase() + " · Вертикальный",
      width: Math.round((widthMM * DOCUMENT_DPI) / 25.4),
      height: Math.round((heightMM * DOCUMENT_DPI) / 25.4),
      widthMM,
      heightMM,
      unit: "mm",
      image: "print-portrait",
      badge: code.toUpperCase(),
      size: widthMM + " × " + heightMM + " мм",
      feature: description,
    },
    {
      id: code + "-landscape",
      category: "print",
      title: code.toUpperCase() + " · Горизонтальный",
      width: Math.round((heightMM * DOCUMENT_DPI) / 25.4),
      height: Math.round((widthMM * DOCUMENT_DPI) / 25.4),
      widthMM: heightMM,
      heightMM: widthMM,
      unit: "mm",
      image: "print-landscape",
      badge: code.toUpperCase(),
      size: heightMM + " × " + widthMM + " мм",
      feature: description,
    },
  ];
  const digital = (
    id: string,
    category: string,
    title: string,
    width: number,
    height: number,
    image: string,
    feature: string,
  ) => ({
    id,
    category,
    title,
    width,
    height,
    image,
    feature,
    unit: "px",
    size: width + " × " + height + " px",
  });
  const formats: Format[] = [
    ...paper(
      "a4",
      210,
      297,
      "Документы, рабочие листы и материалы для занятий.",
    ),
    ...paper(
      "a5",
      148,
      210,
      "Открытки, небольшие листовки и страницы блокнота.",
    ),
    ...paper(
      "a3",
      297,
      420,
      "Плакаты, наглядные пособия и большие иллюстрации.",
    ),
    digital(
      "vk-cover",
      "vk",
      "Обложка сообщества",
      1920,
      768,
      "community-cover",
      "На телефоне края обрезаются. Размещайте важное по центру.",
    ),
    digital(
      "vk-live",
      "vk",
      "Живая обложка",
      1080,
      1920,
      "vertical-phone",
      "Вертикальный кадр для обложки в мобильном приложении VK.",
    ),
    digital(
      "vk-avatar",
      "vk",
      "Аватар",
      400,
      400,
      "avatar",
      "VK обрезает изображение в круг. Минимальный размер — 200 × 200 px.",
    ),
    digital(
      "vk-square",
      "vk",
      "Пост · Квадрат",
      1080,
      1080,
      "square-post",
      "Универсальный квадратный формат для публикаций в ленте.",
    ),
    digital(
      "vk-landscape",
      "vk",
      "Пост · Горизонтальный",
      900,
      600,
      "landscape-post",
      "Соотношение сторон 3:2. Для фотографий и иллюстраций в ленте.",
    ),
    digital(
      "vk-story",
      "vk",
      "История",
      1080,
      1920,
      "vertical-phone",
      "Важное — по центру: сверху и снизу часть кадра закрывает интерфейс.",
    ),
    digital(
      "vk-clip",
      "vk",
      "Клипы и вертикальное видео",
      1080,
      1920,
      "vertical-phone",
      "Соотношение сторон 9:16. Для обложек и кадров вертикального видео.",
    ),
    digital(
      "vk-video-hd",
      "vk",
      "Обложка видео · HD",
      1280,
      720,
      "video-cover",
      "Горизонтальная обложка с соотношением сторон 16:9.",
    ),
    digital(
      "vk-video-fullhd",
      "vk",
      "Обложка видео · Full HD",
      1920,
      1080,
      "video-cover",
      "Более крупный вариант обложки видео в формате 16:9.",
    ),
    digital(
      "web-story",
      "web",
      "История",
      1080,
      1920,
      "vertical-phone",
      "Вертикальный формат 9:16 для просмотра на телефоне.",
    ),
    digital(
      "web-screen",
      "web",
      "Экран",
      1920,
      1080,
      "web-screen",
      "Широкий формат 16:9 для презентаций и экранных материалов.",
    ),
    digital(
      "web-square",
      "web",
      "Квадрат",
      1080,
      1080,
      "square-post",
      "Соотношение сторон 1:1. Для карточек, публикаций и иллюстраций.",
    ),
  ];
  const tabs = [
    { id: "print", title: "Для печати" },
    { id: "vk", title: "VK" },
    { id: "web", title: "Веб" },
  ];
  const checkIcon =
    '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 10 3 3 7-7"/></svg>';
  const dialog = document.createElement("dialog");
  dialog.id = "screen-format-picker";
  dialog.className = "format-picker";
  dialog.setAttribute("aria-labelledby", "format-picker-title");
  dialog.innerHTML = `
        <div class="format-picker-shell">
            <header class="format-picker-header">
                <div><p class="format-picker-eyebrow">Размер холста</p><h2 id="format-picker-title">Выберите формат</h2></div>
                <button type="button" class="format-picker-close" aria-label="Закрыть выбор формата"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button>
            </header>
            <div class="format-picker-tabs" role="tablist" aria-label="Категории форматов">
                ${tabs.map((tab) => `<button type="button" role="tab" id="format-tab-${tab.id}" aria-controls="format-panel-${tab.id}" data-format-tab="${tab.id}">${tab.title}</button>`).join("")}
            </div>
            <div class="format-picker-body">
                ${tabs
                  .map(
                    (
                      tab,
                    ) => `<section class="format-picker-panel" id="format-panel-${tab.id}" role="tabpanel" aria-labelledby="format-tab-${tab.id}" hidden>
                    <div class="format-picker-grid" role="radiogroup" aria-label="${tab.title}">
                        ${formats
                          .filter((format) => format.category === tab.id)
                          .map(
                            (format) => `
                            <button type="button" class="format-picker-card" role="radio" aria-checked="false" tabindex="-1" data-format-id="${format.id}"
                                aria-labelledby="format-name-${format.id} format-size-${format.id}" aria-describedby="format-feature-${format.id}">
                                <span class="format-picker-art"><img src="${assetRoot + format.image}.webp" alt="" width="256" height="256" loading="lazy" decoding="async">
                                    ${format.badge ? `<span class="format-picker-paper-badge">${format.badge}</span>` : ""}
                                    <span class="format-picker-check">${checkIcon}</span>
                                </span>
                                <span class="format-picker-card-copy"><span class="format-picker-card-title" id="format-name-${format.id}">${format.title}</span>
                                    <span class="format-picker-size" id="format-size-${format.id}">${format.size}</span>
                                    <span class="format-picker-feature" id="format-feature-${format.id}">${format.feature}</span>
                                </span>
                            </button>`,
                          )
                          .join("")}
                    </div>
                </section>`,
                  )
                  .join("")}
            </div>
            <footer class="format-picker-footer">
                <p class="format-picker-selection" aria-live="polite">Выберите подходящую карточку</p>
                <div class="format-picker-actions"><button type="button" class="format-picker-cancel">Отмена</button><button type="button" class="format-picker-confirm" disabled>Выбрать формат</button></div>
            </footer>
        </div>`;
  document.body.appendChild(dialog);
  const confirm = dialog.querySelector<HTMLButtonElement>(
    ".format-picker-confirm",
  )!;
  const selectedCopy = dialog.querySelector<HTMLButtonElement>(
    ".format-picker-selection",
  )!;
  const remembered = new Map<number, string>();
  let activeTab = "print";
  let pendingId: string | null = null;
  let openedScreenId: number | null = null;
  let currentSize: ReturnType<FormatPickerPort["read"]> | null = null;
  let pointerStartedOutside = false;

  function matches(format: Format, width: number, height: number) {
    return (
      format.width === Math.round(width) && format.height === Math.round(height)
    );
  }
  function matchingFormat(width: number, height: number) {
    const preferred = formats.find(
      (format) => format.id === remembered.get(port.read().generation),
    );
    if (preferred && matches(preferred, width, height)) return preferred;
    return (
      formats.find(
        (format) =>
          format.category === "print" && matches(format, width, height),
      ) ||
      formats.find(
        (format) => format.category === "web" && matches(format, width, height),
      ) ||
      formats.find((format) => matches(format, width, height))
    );
  }
  function sync(width: number, height: number) {
    const format = matchingFormat(width, height);
    el("screen-format-picker-current").textContent = format
      ? format.title
      : "Произвольный размер";
  }
  function updateSelection() {
    const format = formats.find((item) => item.id === pendingId);
    const cards = [...dialog.querySelectorAll<HTMLElement>(`[data-format-id]`)];
    cards.forEach((card) => {
      const selected = card.dataset.formatId === pendingId;
      card.setAttribute("aria-checked", String(selected));
      card.tabIndex = selected ? 0 : -1;
    });
    const activeCards = dialog.querySelectorAll<HTMLElement>(
      `#format-panel-${activeTab} [data-format-id]`,
    );
    if (![...activeCards].some((card) => card.tabIndex === 0))
      activeCards[0]!.tabIndex = 0;
    confirm.disabled = !format;
    selectedCopy.textContent = format
      ? format.title + " · " + format.size
      : "Выберите подходящую карточку";
  }
  function selectTab(id: string, focus = false) {
    activeTab = id;
    dialog.querySelectorAll<HTMLElement>("[data-format-tab]").forEach((tab) => {
      const active = tab.dataset.formatTab === id;
      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
      el(tab.getAttribute("aria-controls")!).hidden = !active;
      if (active && focus) tab.focus();
    });
    const pending = formats.find(
      (format) => format.id === pendingId && format.category === id,
    );
    if (!pending)
      pendingId =
        formats.find(
          (format) =>
            format.category === id &&
            currentSize &&
            matches(format, currentSize.width, currentSize.height),
        )?.id || null;
    dialog.querySelector<HTMLButtonElement>(".format-picker-body")!.scrollTop =
      0;
    updateSelection();
  }
  function close(reason = "cancel") {
    if (dialog.open) dialog.close(reason);
  }
  trigger.addEventListener("click", () => {
    if (dialog.open) return;
    currentSize = port.read();
    openedScreenId = port.read().generation;
    const format = matchingFormat(currentSize.width, currentSize.height);
    pendingId = format?.id || null;
    selectTab(format?.category || activeTab);
    trigger.setAttribute("aria-expanded", "true");
    document
      .querySelector('[data-property-source="screen-format-picker-open"]')
      ?.setAttribute("aria-expanded", "true");
    dialog.returnValue = "cancel";
    dialog.showModal();
    dialog
      .querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]')!
      .focus();
  });
  dialog
    .querySelector<HTMLButtonElement>(".format-picker-close")!
    .addEventListener("click", () => close());
  dialog
    .querySelector<HTMLButtonElement>(".format-picker-cancel")!
    .addEventListener("click", () => close());
  dialog.addEventListener("close", () => {
    trigger.setAttribute("aria-expanded", "false");
    pendingId = null;
    const apply = el("native-page-apply");
    const selected =
      dialog.returnValue === "selected" && apply && !apply.hidden;
    (selected ? apply : trigger).focus({ preventScroll: true });
  });
  const isOutside = (event: MouseEvent) => {
    const bounds = dialog.getBoundingClientRect();
    return (
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom
    );
  };
  dialog.addEventListener("pointerdown", (event) => {
    pointerStartedOutside = event.target === dialog && isOutside(event);
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog && pointerStartedOutside && isOutside(event))
      close();
    pointerStartedOutside = false;
    const tab = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-format-tab]",
    );
    if (tab) selectTab(tab.dataset.formatTab!);
    const card = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-format-id]",
    );
    if (card) {
      pendingId = card.dataset.formatId!;
      updateSelection();
    }
  });
  dialog
    .querySelector<HTMLButtonElement>(".format-picker-tabs")!
    .addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
        return;
      event.preventDefault();
      const index = tabs.findIndex((tab) => tab.id === activeTab);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? tabs.length - 1
            : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) %
              tabs.length;
      selectTab(tabs[next]!.id, true);
    });
  dialog.querySelectorAll<HTMLElement>(".format-picker-grid").forEach((grid) =>
    grid.addEventListener("keydown", (event) => {
      if (
        !(event.target as HTMLElement).matches("[data-format-id]") ||
        ![
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
        ].includes(event.key)
      )
        return;
      event.preventDefault();
      const cards = [...grid.querySelectorAll<HTMLElement>("[data-format-id]")];
      const index = cards.indexOf(event.target as HTMLElement);
      const columns =
        getComputedStyle(grid).gridTemplateColumns.split(" ").length;
      const step =
        event.key === "ArrowLeft"
          ? -1
          : event.key === "ArrowRight"
            ? 1
            : event.key === "ArrowUp"
              ? -columns
              : columns;
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? cards.length - 1
            : (index + step + cards.length) % cards.length;
      pendingId = cards[next]!.dataset.formatId!;
      updateSelection();
      cards[next]!.focus();
    }),
  );
  confirm.addEventListener("click", () => {
    const format = formats.find((item) => item.id === pendingId);
    if (!format || openedScreenId !== port.read().generation) {
      close();
      return;
    }
    remembered.set(port.read().generation, format.id);
    port.choose(format);
    close("selected");
  });
  const values = port.read();
  sync(values.width, values.height);
  return {
    sync,
    physical(width: number, height: number, unit: string) {
      if (!["mm", "cm"].includes(unit)) return null;
      const format = formats.find(
        (f) => f.category === "print" && matches(f, width, height),
      );
      if (!format?.widthMM || !format.heightMM) return null;
      const divisor = unit === "cm" ? 10 : 1;
      return {
        width: format.widthMM / divisor,
        height: format.heightMM / divisor,
      };
    },
    get open() {
      return dialog.open;
    },
  };
}
